import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square } from 'lucide-react';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const hasNativeBridge = typeof window.BrainSyncVoice?.isAvailable === 'function';

export const VOICE_SUPPORTED = !!SpeechRecognition || hasNativeBridge;

export function VoiceRecorder({ onTranscript, disabled = false }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [duration, setDuration] = useState(0);
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const restartRef = useRef(false);
  const fullTextRef = useRef('');

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    restartRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
    stopTimer();
  }, [stopTimer]);

  // Native bridge handler for TWA
  useEffect(() => {
    if (!hasNativeBridge) return;
    const handler = (e) => {
      const { text, cancelled } = e.detail || {};
      setListening(false);
      stopTimer();
      if (text && !cancelled) {
        const updated = fullTextRef.current
          ? fullTextRef.current + ' ' + text.trim()
          : text.trim();
        fullTextRef.current = updated;
        setTranscript(updated);
        setInterim('');
        onTranscript(updated);
      }
    };
    window.addEventListener('brainsync-voice-result', handler);
    return () => window.removeEventListener('brainsync-voice-result', handler);
  }, [onTranscript, stopTimer]);

  const startNative = useCallback(() => {
    if (disabled) return;
    setListening(true);
    setDuration(0);
    if (!fullTextRef.current) {
      setTranscript('');
      setInterim('');
      fullTextRef.current = '';
    }
    timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    try { window.BrainSyncVoice.startListening(); } catch (e) { setListening(false); stopTimer(); }
  }, [disabled, stopTimer]);

  const start = useCallback(() => {
    if (disabled) return;

    // Use native bridge in TWA
    if (hasNativeBridge) {
      startNative();
      return;
    }

    if (!SpeechRecognition) return;

    setTranscript('');
    setInterim('');
    setDuration(0);
    fullTextRef.current = '';

    const lang = navigator.language || 'en-US';

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event) => {
      let currentInterim = '';
      let currentFinal = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          currentFinal += t;
        } else {
          currentInterim += t;
        }
      }
      if (currentFinal) {
        fullTextRef.current += (fullTextRef.current ? ' ' : '') + currentFinal.trim();
        setTranscript(fullTextRef.current);
        setInterim('');
        onTranscript(fullTextRef.current);
      } else {
        setInterim(currentInterim);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') {
        if (restartRef.current) {
          try { recognition.start(); } catch (e) { /* ignore */ }
        }
        return;
      }
      if (event.error !== 'aborted') {
        // Voice recorder error
      }
      stop();
    };

    recognition.onend = () => {
      if (restartRef.current) {
        try {
          const r = new SpeechRecognition();
          r.continuous = true;
          r.interimResults = true;
          r.lang = lang;
          r.onresult = recognition.onresult;
          r.onerror = recognition.onerror;
          r.onend = recognition.onend;
          recognitionRef.current = r;
          r.start();
        } catch (e) {
          stop();
        }
      } else {
        setListening(false);
      }
    };

    recognitionRef.current = recognition;
    restartRef.current = true;
    recognition.start();
    setListening(true);

    timerRef.current = setInterval(() => {
      setDuration(d => d + 1);
    }, 1000);
  }, [disabled, onTranscript, stop, startNative]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      restartRef.current = false;
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      stopTimer();
    };
  }, [stopTimer]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (!VOICE_SUPPORTED) return null;

  return (
    <div className="flex flex-col items-center gap-4" data-testid="voice-recorder">
      {/* Mic button */}
      <button
        type="button"
        onClick={listening ? (hasNativeBridge ? () => setListening(false) : stop) : start}
        disabled={disabled}
        className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
          listening
            ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 scale-105'
            : 'bg-primary/10 text-primary hover:bg-primary/20 hover:scale-105'
        }`}
        data-testid="voice-record-btn"
      >
        {listening && (
          <>
            <span className="absolute inset-0 rounded-full bg-red-500/30 animate-ping" />
            <span className="absolute inset-[-8px] rounded-full border-2 border-red-400/40 animate-pulse" />
          </>
        )}
        {listening ? (
          <Square className="w-7 h-7 relative z-10 fill-current" />
        ) : (
          <Mic className="w-8 h-8 relative z-10" />
        )}
      </button>

      {/* Status */}
      <div className="text-center">
        {listening ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-600 font-medium">Listening... {formatTime(duration)}</span>
          </div>
        ) : transcript ? (
          <p className="text-sm text-muted-foreground">Tap the mic to continue, or save below</p>
        ) : (
          <p className="text-sm text-muted-foreground">Tap the mic and start speaking</p>
        )}
      </div>

      {/* Transcript preview */}
      {(transcript || interim) && (
        <div className="w-full rounded-xl bg-secondary/50 p-3 max-h-[200px] overflow-y-auto">
          <p className="text-sm leading-relaxed">
            {transcript}
            {interim && <span className="text-muted-foreground/60 italic"> {interim}</span>}
          </p>
        </div>
      )}
    </div>
  );
}
